import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  Grid,
  Paper,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  PlayArrowOutlined as PlayArrowOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  MoreVert as MoreVertIcon,
  FileCopyOutlined as FileCopyOutlinedIcon,
  SecurityOutlined as SecurityOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@material-ui/icons";
import FormPageLayout from "./FormPageLayout";
import {
  copyTemplate,
  deleteTemplate,
  grantTemplatePermission,
  listTemplatePermissions,
  listTemplateUsers,
  listTemplates,
  patchTemplateVisibility,
  revokeTemplatePermission,
  updateTemplatePermission,
} from "./formTemplatesApi";

const useStyles = makeStyles((theme) => ({
  topBar: {
    marginBottom: theme.spacing(1),
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: "#4f6790",
  },
  createButton: {
    borderRadius: 10,
    minWidth: 160,
    textTransform: "none",
    fontWeight: 600,
  },
  filtersRow: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tablePaper: {
    borderRadius: 10,
    overflow: "hidden",
  },
  tableHead: {
    padding: "12px 10px",
    backgroundColor: "#fafbfd",
    color: "#4f6790",
    borderBottom: "1px solid #e4e8f0",
    fontWeight: 600,
  },
  row: {
    minHeight: 64,
    padding: "10px 10px",
  },
  templateName: {
    fontWeight: 600,
  },
  dateText: {
    color: "#4f6790",
  },
  actionUseButton: {
    borderRadius: 8,
    textTransform: "none",
    minWidth: 84,
  },
  actionIcon: {
    color: "#1f2a44",
  },
  actionMenuItem: {
    minWidth: 180,
  },
  dangerText: {
    color: "#d32f2f",
  },
  permissionsSubtitle: {
    color: "#4f6790",
    marginBottom: theme.spacing(0.5),
  },
  permissionsMeta: {
    marginBottom: theme.spacing(2),
    color: "#4f6790",
  },
  shareRow: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  shareButton: {
    textTransform: "none",
    borderRadius: 8,
    minWidth: 120,
  },
  closeButton: {
    textTransform: "none",
    borderRadius: 8,
    minWidth: 100,
  },
  tableCellCompact: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  emptyState: {
    padding: theme.spacing(2),
    color: "#666",
  },
}));

const toBool = (value) => Number(value) === 1 || value === true;

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  return new Date(value).toLocaleDateString("pl-PL");
};

const getCurrentUsername = () => {
  const token = localStorage.getItem("token");
  if (!token) {
    return "";
  }

  try {
    const decoded = jwtDecode(token);
    return decoded?.username || "";
  } catch (error) {
    console.error("Unable to decode token for username:", error);
    return "";
  }
};

const FormTemplatesListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUsername = getCurrentUsername();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionTemplate, setActionTemplate] = useState(null);

  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [permissionsTemplate, setPermissionsTemplate] = useState(null);
  const [permissionsList, setPermissionsList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [shareUserId, setShareUserId] = useState("");
  const [sharePermission, setSharePermission] = useState("READ");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);

  const usersById = useMemo(() => {
    return new Map(usersList.map((user) => [Number(user.id), user]));
  }, [usersList]);

  const assignedPermissionUserIds = useMemo(() => {
    return new Set(permissionsList.map((item) => Number(item.user_id)));
  }, [permissionsList]);

  const shareCandidates = useMemo(() => {
    if (!permissionsTemplate) {
      return [];
    }

    return usersList.filter((user) => {
      const userId = Number(user.id);
      const isOwner =
        String(user.username || "") === String(permissionsTemplate.owner || "");
      return !isOwner && !assignedPermissionUserIds.has(userId);
    });
  }, [usersList, assignedPermissionUserIds, permissionsTemplate]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await listTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Nie udało się pobrać szablonów.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersList = async () => {
    try {
      const users = await listTemplateUsers();
      setUsersList(Array.isArray(users) ? users : []);
    } catch (error) {
      console.error("Error fetching users list:", error);
      toast.error("Nie udało się pobrać listy użytkowników.");
    }
  };

  const fetchPermissionsForTemplate = async (templateId) => {
    setPermissionsLoading(true);
    try {
      const permissions = await listTemplatePermissions(templateId);
      setPermissionsList(Array.isArray(permissions) ? permissions : []);
    } catch (error) {
      console.error("Error loading permissions list:", error);
      toast.error("Nie udało się pobrać uprawnień.");
      setPermissionsList([]);
    } finally {
      setPermissionsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (location.state?.toastMessage) {
      toast.success(location.state.toastMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const filteredTemplates = useMemo(() => {
    const filtered = templates.filter((template) => {
      const matchesSearch = String(template.description || "")
        .toLowerCase()
        .includes(searchText.toLowerCase());

      const matchesScope =
        scopeFilter === "all" ||
        (scopeFilter === "mine" && template.owner === currentUsername) ||
        (scopeFilter === "shared" && template.owner !== currentUsername);

      const matchesVisibility =
        visibilityFilter === "all" ||
        (visibilityFilter === "public" && toBool(template.is_public)) ||
        (visibilityFilter === "private" && !toBool(template.is_public));

      return matchesSearch && matchesScope && matchesVisibility;
    });

    return filtered.sort((a, b) => {
      const aDate = new Date(a.updated_at || a.created_at || 0).getTime();
      const bDate = new Date(b.updated_at || b.created_at || 0).getTime();
      return sortOrder === "oldest" ? aDate - bDate : bDate - aDate;
    });
  }, [
    templates,
    searchText,
    scopeFilter,
    visibilityFilter,
    sortOrder,
    currentUsername,
  ]);

  const closeActionMenu = () => {
    setActionAnchorEl(null);
    setActionTemplate(null);
  };

  const openActionMenu = (event, template) => {
    setActionAnchorEl(event.currentTarget);
    setActionTemplate(template);
  };

  const withSelectedTemplate = async (action) => {
    if (!actionTemplate) {
      return;
    }

    const selectedTemplate = actionTemplate;
    closeActionMenu();
    await action(selectedTemplate);
  };

  const handleUseTemplate = (templateId) => {
    navigate(`/form?templateId=${templateId}&fromTemplates=1`);
  };

  const handleCopy = async (template) => {
    try {
      await copyTemplate(
        template.id,
        `${template.description || "Szablon"} - kopia`,
        false,
      );
      toast.success("Utworzono kopię szablonu.");
      await fetchTemplates();
    } catch (error) {
      console.error("Error copying template:", error);
      toast.error("Nie udało się skopiować szablonu.");
    }
  };

  const handleToggleVisibility = async (template) => {
    if (!template.isOwner) {
      toast.warning("Tylko właściciel może zmienić widoczność.");
      return;
    }

    try {
      await patchTemplateVisibility(template.id, !toBool(template.is_public));
      toast.success("Widoczność szablonu została zaktualizowana.");
      await fetchTemplates();

      if (
        permissionsDialogOpen &&
        permissionsTemplate &&
        Number(permissionsTemplate.id) === Number(template.id)
      ) {
        setPermissionsTemplate({
          ...permissionsTemplate,
          is_public: !toBool(template.is_public),
        });
      }
    } catch (error) {
      console.error("Error toggling visibility:", error);
      toast.error("Nie udało się zmienić widoczności.");
    }
  };

  const handleDelete = async (template) => {
    if (!template.isOwner) {
      toast.warning("Tylko właściciel może usunąć szablon.");
      return;
    }

    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingTemplate) {
      return;
    }

    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) {
      return;
    }

    try {
      setDeletingTemplate(true);
      await deleteTemplate(templateToDelete.id);
      toast.success("Szablon został usunięty.");
      await fetchTemplates();
      if (
        permissionsDialogOpen &&
        Number(permissionsTemplate?.id) === Number(templateToDelete.id)
      ) {
        setPermissionsDialogOpen(false);
        setPermissionsTemplate(null);
      }

      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Nie udało się usunąć szablonu.");
    } finally {
      setDeletingTemplate(false);
    }
  };

  const openPermissionsDialog = async (template) => {
    if (!template.isOwner) {
      toast.warning("Tylko właściciel może zarządzać uprawnieniami.");
      return;
    }

    setPermissionsTemplate(template);
    setPermissionsDialogOpen(true);
    setShareUserId("");
    setSharePermission("READ");

    await Promise.all([
      fetchPermissionsForTemplate(template.id),
      usersList.length ? Promise.resolve() : fetchUsersList(),
    ]);
  };

  const closePermissionsDialog = () => {
    setPermissionsDialogOpen(false);
    setPermissionsTemplate(null);
    setPermissionsList([]);
    setShareUserId("");
    setSharePermission("READ");
  };

  const handlePermissionChangeForUser = async (userId, permission) => {
    if (!permissionsTemplate) {
      return;
    }

    try {
      await updateTemplatePermission(
        permissionsTemplate.id,
        userId,
        permission,
      );
      toast.success("Uprawnienie zostało zaktualizowane.");
      await fetchPermissionsForTemplate(permissionsTemplate.id);
    } catch (error) {
      console.error("Error updating permission:", error);
      toast.error("Nie udało się zaktualizować uprawnienia.");
    }
  };

  const handleRevokeForUser = async (userId) => {
    if (!permissionsTemplate) {
      return;
    }

    try {
      await revokeTemplatePermission(permissionsTemplate.id, userId);
      toast.success("Uprawnienie zostało odebrane.");
      await fetchPermissionsForTemplate(permissionsTemplate.id);
    } catch (error) {
      console.error("Error revoking permission:", error);
      toast.error("Nie udało się odebrać uprawnienia.");
    }
  };

  const handleSharePermission = async () => {
    if (!permissionsTemplate || !shareUserId) {
      toast.warning("Wybierz użytkownika.");
      return;
    }

    try {
      await grantTemplatePermission(
        permissionsTemplate.id,
        Number(shareUserId),
        sharePermission,
      );
      toast.success("Uprawnienie zostało nadane.");
      setShareUserId("");
      await fetchPermissionsForTemplate(permissionsTemplate.id);
    } catch (error) {
      console.error("Error granting permission:", error);
      toast.error("Nie udało się nadać uprawnienia.");
    }
  };

  return (
    <FormPageLayout activeItem="templates">
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        className={classes.topBar}
      >
        <Grid item>
          <Typography variant="h4" className={classes.title}>
            Szablony zgłoszeń
          </Typography>
          <Typography variant="subtitle1" className={classes.subtitle}>
            Twórz i zarządzaj szablonami wykorzystywanymi podczas dodawania
            odpraw.
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            className={classes.createButton}
            onClick={() => navigate("/form/templates/new")}
          >
            Utwórz szablon
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} className={classes.filtersRow}>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Szukaj po nazwie..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            fullWidth
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="templates-scope-label">Zakres</InputLabel>
            <Select
              labelId="templates-scope-label"
              value={scopeFilter}
              onChange={(event) => setScopeFilter(String(event.target.value))}
              label="Zakres"
            >
              <MenuItem value="all">Wszystkie</MenuItem>
              <MenuItem value="mine">Moje</MenuItem>
              <MenuItem value="shared">Udostępnione</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="templates-visibility-label">Widoczność</InputLabel>
            <Select
              labelId="templates-visibility-label"
              value={visibilityFilter}
              onChange={(event) =>
                setVisibilityFilter(String(event.target.value))
              }
              label="Widoczność"
            >
              <MenuItem value="all">Widoczność: wszystkie</MenuItem>
              <MenuItem value="public">Publiczne</MenuItem>
              <MenuItem value="private">Prywatne</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="templates-sort-label">Sortowanie</InputLabel>
            <Select
              labelId="templates-sort-label"
              value={sortOrder}
              onChange={(event) => setSortOrder(String(event.target.value))}
              label="Sortowanie"
            >
              <MenuItem value="newest">Najnowsze najpierw</MenuItem>
              <MenuItem value="oldest">Najstarsze najpierw</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchTemplates}
            disabled={loading}
            fullWidth
          >
            {loading ? "Odświeżanie..." : "Odśwież"}
          </Button>
        </Grid>
      </Grid>

      <Paper variant="outlined" className={classes.tablePaper}>
        <Grid container className={classes.tableHead}>
          <Grid item xs={12} sm={3}>
            Nazwa
          </Grid>
          <Grid item xs={12} sm={2}>
            Właściciel
          </Grid>
          <Grid item xs={12} sm={2}>
            Widoczność
          </Grid>
          <Grid item xs={12} sm={2}>
            Moje uprawnienia
          </Grid>
          <Grid item xs={12} sm={1}>
            Data modyfikacji
          </Grid>
          <Grid item xs={12} sm={2}>
            Akcje
          </Grid>
        </Grid>

        {filteredTemplates.map((template) => {
          const canEdit = Boolean(template.canEdit || template.isOwner);
          const permissionLabel = template.isOwner
            ? "Właściciel"
            : canEdit
              ? "Edycja"
              : "Odczyt";

          return (
            <Box key={template.id}>
              <Grid container alignItems="center" className={classes.row}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body1" className={classes.templateName}>
                    {template.description || `Szablon #${template.id}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  {template.owner === currentUsername
                    ? "Ty"
                    : template.owner || "-"}
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Chip
                    size="small"
                    label={
                      toBool(template.is_public) ? "Publiczny" : "Prywatny"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Chip size="small" label={permissionLabel} />
                </Grid>
                <Grid item xs={12} sm={1} className={classes.dateText}>
                  {formatDate(template.updated_at || template.created_at)}
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        className={classes.actionUseButton}
                        startIcon={<PlayArrowOutlinedIcon fontSize="small" />}
                        onClick={() => handleUseTemplate(template.id)}
                      >
                        Użyj
                      </Button>
                    </Grid>
                    {canEdit && (
                      <Grid item>
                        <IconButton
                          size="small"
                          className={classes.actionIcon}
                          onClick={() =>
                            navigate(`/form/templates/${template.id}/edit`)
                          }
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    )}
                    <Grid item>
                      <IconButton
                        size="small"
                        className={classes.actionIcon}
                        onClick={(event) => openActionMenu(event, template)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          );
        })}

        {!filteredTemplates.length && (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              Brak szablonów spełniających kryteria.
            </Typography>
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={closeActionMenu}
        keepMounted
      >
        <MenuItem
          className={classes.actionMenuItem}
          onClick={() => withSelectedTemplate(handleCopy)}
        >
          <FileCopyOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          Utwórz kopię
        </MenuItem>
        <MenuItem
          className={classes.actionMenuItem}
          onClick={() =>
            withSelectedTemplate(async (template) => {
              await openPermissionsDialog(template);
            })
          }
          disabled={!actionTemplate?.isOwner}
        >
          <SecurityOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          Uprawnienia
        </MenuItem>
        <MenuItem
          className={classes.actionMenuItem}
          onClick={() => withSelectedTemplate(handleToggleVisibility)}
          disabled={!actionTemplate?.isOwner}
        >
          <VisibilityOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          Zmień widoczność
        </MenuItem>
        <MenuItem
          className={classes.actionMenuItem}
          onClick={() => withSelectedTemplate(handleDelete)}
          disabled={!actionTemplate?.isOwner}
        >
          <DeleteOutlineIcon
            fontSize="small"
            style={{ marginRight: 8 }}
            className={classes.dangerText}
          />
          <span className={classes.dangerText}>Usuń</span>
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Usunąć szablon?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 8 }}>
            Tej operacji nie można cofnąć.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Szablon: {templateToDelete?.description || "(bez nazwy)"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDeleteDialog}
            color="default"
            disabled={deletingTemplate}
          >
            Anuluj
          </Button>
          <Button
            onClick={confirmDeleteTemplate}
            variant="contained"
            style={{ backgroundColor: "#d32f2f", color: "#fff" }}
            disabled={deletingTemplate}
          >
            {deletingTemplate ? "Usuwanie..." : "Usuń szablon"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={permissionsDialogOpen}
        onClose={closePermissionsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Uprawnienia do szablonu</DialogTitle>
        <DialogContent>
          {permissionsTemplate && (
            <>
              <Typography className={classes.permissionsSubtitle}>
                Szablon:{" "}
                {permissionsTemplate.description ||
                  `Szablon #${permissionsTemplate.id}`}
              </Typography>
              <Typography className={classes.permissionsMeta}>
                Właściciel:{" "}
                {permissionsTemplate.owner === currentUsername
                  ? "Ty"
                  : permissionsTemplate.owner}{" "}
                | Widoczność:{" "}
                {toBool(permissionsTemplate.is_public)
                  ? "Publiczny"
                  : "Prywatny"}
              </Typography>
            </>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Użytkownik</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Uprawnienie</TableCell>
                <TableCell align="center">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissionsLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className={classes.tableCellCompact}>
                    Ładowanie uprawnień...
                  </TableCell>
                </TableRow>
              ) : permissionsList.length ? (
                permissionsList.map((permission) => {
                  const user = usersById.get(Number(permission.user_id));
                  return (
                    <TableRow key={permission.user_id}>
                      <TableCell className={classes.tableCellCompact}>
                        {permission.username ||
                          user?.username ||
                          permission.user_id}
                      </TableCell>
                      <TableCell className={classes.tableCellCompact}>
                        {user?.email || "-"}
                      </TableCell>
                      <TableCell className={classes.tableCellCompact}>
                        <FormControl variant="outlined" size="small" fullWidth>
                          <Select
                            value={String(permission.permission || "READ")}
                            onChange={(event) =>
                              handlePermissionChangeForUser(
                                permission.user_id,
                                String(event.target.value),
                              )
                            }
                          >
                            <MenuItem value="READ">Odczyt</MenuItem>
                            <MenuItem value="EDIT">Edycja</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell
                        align="center"
                        className={classes.tableCellCompact}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleRevokeForUser(permission.user_id)
                          }
                        >
                          <DeleteOutlineIcon
                            fontSize="small"
                            className={classes.dangerText}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className={classes.tableCellCompact}>
                    Brak dodatkowych uprawnień dla tego szablonu.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Typography variant="subtitle1" className={classes.shareRow}>
            Udostępnij użytkownikowi
          </Typography>

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="share-user-label">
                  Wybierz użytkownika
                </InputLabel>
                <Select
                  labelId="share-user-label"
                  value={shareUserId}
                  onChange={(event) =>
                    setShareUserId(String(event.target.value))
                  }
                  label="Wybierz użytkownika"
                >
                  {shareCandidates.map((user) => (
                    <MenuItem key={user.id} value={String(user.id)}>
                      {user.username}
                      {user.email ? ` (${user.email})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="share-permission-label">Uprawnienie</InputLabel>
                <Select
                  labelId="share-permission-label"
                  value={sharePermission}
                  onChange={(event) =>
                    setSharePermission(String(event.target.value))
                  }
                  label="Uprawnienie"
                >
                  <MenuItem value="READ">Odczyt</MenuItem>
                  <MenuItem value="EDIT">Edycja</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                color="primary"
                className={classes.shareButton}
                onClick={handleSharePermission}
                fullWidth
              >
                Udostępnij
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closePermissionsDialog}
            className={classes.closeButton}
          >
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-center" />
    </FormPageLayout>
  );
};

export default FormTemplatesListPage;
